import React, { useState, useEffect } from 'react'
import HighchartsReact from 'highcharts-react-official'
import Highcharts from 'highcharts'
import { getValue } from '../../../../Utility/SharedUtility'
import { DimensionNames } from '../../../../Constants/DimensionNames'
import {
  CHART_TABLE_OUTER_BORDER_COLOR,
  computeHeatmapLayoutMetrics,
  createXAxisLabelsFormatter,
  createYAxisLabelsFormatter,
  HEATMAP_MARGIN_LEFT,
  HEATMAP_MARGIN_RIGHT,
  HeatmapPreviewPlaceholder,
  resolveYearSelectionContext,
  TOTAL_BLOCK_SEPARATOR_COLOR,
  X_AXIS_TILTED_LABEL_DISTANCE_PX,
  X_AXIS_TILTED_LABEL_ROTATION_DEG,
  Y_AXIS_LABEL_WIDTH_PX,
} from './NetFlowsHeatmapShared'

export function formatGrowthLabel(value, isSingleYear) {
  if (value == null || Number.isNaN(value)) return 'n/a'
  const pct = `${Number(value).toFixed(1)}%`
  return isSingleYear ? `YoY growth: ${pct}` : `CAGR: ${pct}`
}

function createBucketColorResolver(colorMap, bucketRules) {
  const resolveBucketKey = (value) => {
    if (value == null || Number.isNaN(value)) return 'na'
    if (value < 0) return 'belowZero'
    for (const rule of bucketRules) {
      if (value >= rule.min && value < rule.max) return rule.key
    }
    return bucketRules[bucketRules.length - 1]?.key ?? 'na'
  }

  return {
    getCellColor: (value) => colorMap[resolveBucketKey(value)]?.color ?? colorMap.na.color,
    getFontColor: (value) => colorMap[resolveBucketKey(value)]?.fontColor ?? colorMap.na.fontColor,
  }
}

const TOTAL_CELL_STYLE = {
  value: null,
  color: 'rgb(255, 255, 255)',
  fillColor: '#000000',
}

function buildTotalPoint({ x, y, displayValue, hoverText }) {
  return {
    x,
    y,
    ...TOTAL_CELL_STYLE,
    customText: displayValue,
    hoverText,
  }
}

export function GrowthMetricHeatmapChart({
  chart,
  chartData,
  chartRef,
  selectedAttributeNamesByDimension,
  rowAxisLabel,
  resolveFilterSubtitleLabel,
  accessibilityPointDescription,
  config,
}) {
  const [chartOptions, setChartOptions] = useState(null)
  const {
    FirstYear,
    hasValidYearSelection,
    yearsLabel,
    endYear,
    isSingleYear,
  } = resolveYearSelectionContext(selectedAttributeNamesByDimension, chartData)

  const filterSubtitleLabel = resolveFilterSubtitleLabel(selectedAttributeNamesByDimension)
  const currencyLabel = selectedAttributeNamesByDimension?.[DimensionNames.ConversionID]?.[0]
    ?? selectedAttributeNamesByDimension?.['Currency']?.[0]
    ?? ''
  const headerSubtitle = [filterSubtitleLabel, currencyLabel].filter(Boolean).length > 0
    ? `${filterSubtitleLabel}, ${currencyLabel}`
    : ''

  const headerTitle = endYear != null ? config.getHeaderTitle(endYear) : ''
  const legendTitleText = endYear != null
    ? (isSingleYear ? config.getLegendTitleSingle(FirstYear) : config.getLegendTitleMulti(yearsLabel))
    : ''

  useEffect(() => {
    if (!chartData || !hasValidYearSelection) {
      setChartOptions(null)
      return
    }

    const { getCellColor, getFontColor } = createBucketColorResolver(config.colorMap, config.bucketRules)

    const baseYCategories = chartData.yAttributes?.map((item) => item.name) ?? []
    const baseXCategories = chartData.xAttributes?.map((item) => item.name) ?? []
    const numBaseRows = baseYCategories.length
    const numBaseCols = baseXCategories.length
    const totals = chartData.totals ?? {}
    const columnTotal = totals.auMColumnTotal ?? chartData.auMColumnTotal ?? []
    const rowTotal = totals.auMRowTotal ?? chartData.auMRowTotal ?? []
    const grandTotal = totals.auMGrandTotal ?? chartData.auMGrandTotal

    const totalColumnLabel = config.getTotalColumnLabel(endYear)
    const yCategories = [...baseYCategories, totalColumnLabel]
    const xCategories = [...baseXCategories, totalColumnLabel]

    const totalRowIndex = numBaseRows
    const totalColIndex = numBaseCols
    const totalBlockEdgeX = totalColIndex - 0.5
    const totalBlockEdgeY = totalRowIndex - 0.5

    const {
      hasManyXCategories,
      heatmapChartWidthPx,
      xLabelBoxPx,
      chartMarginTop,
      chartMarginBottom,
      chartHeight,
    } = computeHeatmapLayoutMetrics(xCategories.length, yCategories.length)

    const mainData = (chartData.dataPoints ?? []).map((item) => {
      const value = getValue(item.value)
      const numValue = value != null && !Number.isNaN(value) ? Number(value) : value
      const initialVal = getValue(item.initialValue)
      const yName = baseYCategories[item.yCoordinate] || ''
      const xName = baseXCategories[item.xCoordinate] || ''
      const displayValue = initialVal != null && !Number.isNaN(initialVal)
        ? config.formatDisplayValue(initialVal)
        : 'n/a'
      const growthText = formatGrowthLabel(numValue, isSingleYear)
      return {
        x: item.xCoordinate,
        y: item.yCoordinate,
        value: numValue,
        color: getCellColor(numValue),
        fillColor: getFontColor(numValue),
        customText: displayValue,
        hoverText: item.hoverText || config.buildMainCellHoverText({
          displayValue,
          growthText,
          rowAxisLabel,
          yName,
          xName,
          yearsLabel,
        }),
      }
    })

    const columnTotalPoints = columnTotal.map((val, y) => {
      const displayValue = val != null && !Number.isNaN(val) ? config.formatDisplayValue(val) : 'n/a'
      return buildTotalPoint({
        x: totalColIndex,
        y,
        displayValue,
        hoverText: config.buildColumnTotalHoverText({
          displayValue,
          rowAxisLabel,
          rowName: baseYCategories[y] || '',
        }),
      })
    })

    const rowTotalPoints = rowTotal.map((val, x) => {
      const displayValue = val != null && !Number.isNaN(val) ? config.formatDisplayValue(val) : 'n/a'
      return buildTotalPoint({
        x,
        y: totalRowIndex,
        displayValue,
        hoverText: config.buildRowTotalHoverText({
          displayValue,
          columnName: baseXCategories[x] || '',
        }),
      })
    })

    const grandTotalPoint = (() => {
      const val = grandTotal != null && !Number.isNaN(Number(grandTotal)) ? Number(grandTotal) : null
      const displayValue = val != null ? config.formatDisplayValue(val) : 'n/a'
      return buildTotalPoint({
        x: totalColIndex,
        y: totalRowIndex,
        displayValue,
        hoverText: config.buildGrandTotalHoverText({ displayValue, endYear }),
      })
    })()

    const allData = [...mainData, ...columnTotalPoints, ...rowTotalPoints, grandTotalPoint]

    setChartOptions({
      offlineExporting: { enabled: true },
      chart: {
        type: 'heatmap',
        width: heatmapChartWidthPx,
        height: chartHeight,
        marginLeft: HEATMAP_MARGIN_LEFT,
        marginRight: HEATMAP_MARGIN_RIGHT,
        marginTop: chartMarginTop,
        marginBottom: chartMarginBottom,
        spacingTop: hasManyXCategories ? 6 : 0,
        spacingBottom: 16,
        plotBorderWidth: 1,
        plotBorderColor: CHART_TABLE_OUTER_BORDER_COLOR,
        plotBackgroundColor: '#ffffff',
        exportEnabled: true,
      },
      title: {
        text: headerTitle || chart.title + (yearsLabel ? `, ${yearsLabel}` : ''),
        align: 'left',
        style: {
          fontFamily: 'McKinsey Sans Regular',
          color: '#000000',
          textOutline: '0px contrast',
          fontSize: '20px',
          fontWeight: 'normal',
          whiteSpace: 'normal',
          wordWrap: 'break-word',
        },
      },
      subtitle: {
        text: headerSubtitle || undefined,
        align: 'left',
        style: {
          fontFamily: 'McKinsey Sans Regular',
          color: 'rgb(117, 117, 117)',
          textOutline: '0px contrast',
          fontSize: '14px',
          whiteSpace: 'normal',
          wordWrap: 'break-word',
        },
      },
      yAxis: {
        categories: yCategories,
        reversed: true,
        title: { text: null },
        lineWidth: 0,
        gridLineWidth: 0,
        tickLength: 0,
        minorGridLineWidth: 0,
        plotLines: [{
          value: totalBlockEdgeY,
          color: TOTAL_BLOCK_SEPARATOR_COLOR,
          width: 1,
          zIndex: 5,
          dashStyle: 'Solid',
        }],
        labels: {
          useHTML: true,
          formatter: createYAxisLabelsFormatter(Y_AXIS_LABEL_WIDTH_PX),
          style: {
            fontFamily: 'McKinsey Sans Regular',
            width: `${Y_AXIS_LABEL_WIDTH_PX}px`,
            textAlign: 'left',
            whiteSpace: 'normal',
            overflow: 'visible',
            textOverflow: 'clip',
          },
        },
      },
      xAxis: {
        opposite: true,
        offset: hasManyXCategories ? 6 : 0,
        categories: xCategories,
        lineWidth: 0,
        gridLineWidth: 0,
        tickLength: 0,
        tickWidth: 0,
        minorGridLineWidth: 0,
        plotLines: [{
          value: totalBlockEdgeX,
          color: TOTAL_BLOCK_SEPARATOR_COLOR,
          width: 1,
          zIndex: 5,
          dashStyle: 'Solid',
        }],
        labels: {
          align: 'center',
          verticalAlign: 'middle',
          rotation: hasManyXCategories ? X_AXIS_TILTED_LABEL_ROTATION_DEG : 0,
          ...(hasManyXCategories ? { distance: X_AXIS_TILTED_LABEL_DISTANCE_PX } : {}),
          x: 0,
          y: hasManyXCategories ? -18 : 0,
          useHTML: true,
          formatter: createXAxisLabelsFormatter(xLabelBoxPx, 'center', hasManyXCategories),
          style: {
            fontFamily: 'McKinsey Sans Regular',
            width: `${xLabelBoxPx}px`,
            textAlign: 'center',
            whiteSpace: 'normal',
            overflow: 'visible',
            textOverflow: 'clip',
          },
        },
      },
      accessibility: {
        point: {
          descriptionFormat: '{(add index 1)}. ' + accessibilityPointDescription,
        },
        enabled: false,
      },
      colorAxis: {
        min: -10,
        max: 20,
        title: {
          text: config.growthAxisTitle,
          style: { fontFamily: 'McKinsey Sans Regular' },
        },
        dataClasses: config.dataClasses,
      },
      legend: {
        layout: 'horizontal',
        align: 'right',
        verticalAlign: 'top',
        itemStyle: { fontFamily: 'McKinsey Sans Regular' },
        title: {
          align: 'right',
          text: legendTitleText,
          style: {
            fontFamily: 'McKinsey Sans Regular',
            align: 'right',
            fontWeight: 'light',
            fontSize: '14px',
            whiteSpace: 'normal',
            wordWrap: 'break-word',
          },
        },
      },
      tooltip: {
        useHTML: true,
        outside: true,
        headerFormat: '',
        pointFormat: `<div style="font-family: 'McKinsey Sans Regular'; width: 250px; line-height: 1.5; white-space: normal;">{point.hoverText}</div>`,
        style: {
          fontFamily: 'McKinsey Sans Regular',
          lineHeight: '1.5',
        },
      },
      credits: { enabled: false },
      series: [{
        name: config.seriesName,
        borderWidth: 1,
        borderColor: '#fff',
        data: allData,
        dataLabels: {
          enabled: true,
          format: '<span style="width : 250px; fill: {point.fillColor}; font-family: \'McKinsey Sans Regular\';">{point.customText}</span>',
          style: {
            textOutline: '0px contrast',
            fontSize: '12px',
            fontWeight: '100',
            fontFamily: 'McKinsey Sans Regular',
            whiteSpace: 'normal',
            wordWrap: 'break-word',
          },
        },
      }],
      exporting: {
        enabled: false,
        fallbackToExportServer: false,
      },
      responsive: {
        rules: [{ condition: { maxWidth: 500 } }],
      },
    })
  }, [
    chartData,
    chart,
    selectedAttributeNamesByDimension,
    hasValidYearSelection,
    yearsLabel,
    legendTitleText,
    headerTitle,
    headerSubtitle,
    endYear,
    isSingleYear,
    rowAxisLabel,
    accessibilityPointDescription,
    config,
  ])

  return (
    <div className="highchart custom-overflow">
      {
        chartData && chartOptions && hasValidYearSelection
          ? <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} />
          : <HeatmapPreviewPlaceholder chart={chart} />
      }
    </div>
  )
}
